import { initData } from "./telegram";

export interface Me {
  id: number;
  tg_id: number;
  username: string | null;
  first_name: string | null;
  role: "athlete" | "coach" | "admin";
  units: "kg" | "lb";
  locale: string;
  /** Оформление: выбор человека, а не результат. «system» — идти за телефоном. */
  theme: "dark" | "light" | "system";
  /** Минуты к UTC. null — устройство ещё не сообщило, и бот молчит. */
  tz_offset_min: number | null;
  /** null — час напоминания не выбран руками, берётся привычный из истории. */
  remind_hour: number | null;
  remind_enabled: boolean;
  digest_enabled: boolean;
  /** false — витрина для друзей выключена: им видно только имя. */
  visible_to_friends: boolean;
  created_at: string;
}

export interface Settings {
  tz_offset_min?: number;
  remind_hour?: number;
  /** Вернуть автоматический час: `remind_hour: undefined` означало бы «не менять». */
  remind_hour_auto?: boolean;
  remind_enabled?: boolean;
  digest_enabled?: boolean;
  visible_to_friends?: boolean;
  theme?: "dark" | "light" | "system";
}

/** Не присланное поле не меняется — шлём только то, что человек тронул. */
export const updateSettings = (patch: Settings) =>
  request<Me>("/me", { method: "PATCH", body: JSON.stringify(patch) });

export interface Exercise {
  id: number;
  slug: string;
  name_ru: string;
  primary_muscle: string;
  secondary_muscles: string[];
  equipment: string;
  movement: string;
  is_custom: boolean;
  /** null — выверенной иллюстрации нет, показываем иконку снаряда. */
  image_url: string | null;
  /** Только в выдаче поиска: отрезки [начало, конец) совпавших слов в name_ru. */
  highlight?: [number, number][];
}

/** Полиморфная цель подхода. `type` определяет, какие поля заполнены. */
export type TargetSpec =
  | { type: "intensity_label"; value: "heavy" | "medium" | "light" | "drop" }
  | { type: "percent_1rm"; percent: number }
  // Из импорта приходит как в файле: 8, «8» или диапазон «6-7».
  | { type: "rpe"; value: number | string }
  | { type: "rir"; value: number | string }
  | { type: "absolute"; weight_kg: number }
  | { type: "free" };

export interface ProgramExercise {
  id: number;
  order_index: number;
  exercise: Exercise;
  target_sets: number | null;
  target_reps_min: number | null;
  target_reps_max: number | null;
  rep_sequence: number[] | null;
  target_spec: TargetSpec;
  rest_sec: number | null;
  notes: string | null;
  superset_group: number | null;
  is_backoff: boolean;
  /** Варианты замены для слота. Порядок задан методикой, а не алфавитом. */
  alternatives: { order_index: number; exercise: Exercise }[];
}

export interface ProgramDay {
  id: number;
  order_index: number;
  name: string;
  notes: string | null;
  exercises: ProgramExercise[];
}

export interface ProgramBlock {
  id: number;
  order_index: number;
  name: string;
  kind: string;
  is_deload: boolean;
  repeat_weeks: number;
  notes: string | null;
  days: ProgramDay[];
}

export interface ProgramCard {
  id: number;
  name: string;
  description: string | null;
  accent_color: string | null;
  source: string;
  is_archived: boolean;
  created_at: string;
  block_count: number;
  day_count: number;
  exercise_count: number;
  /**
   * Имя тренера, если программу назначил он. null — своя собственная.
   * Программа остаётся у тренера: его правки доходят до всех учеников разом.
   */
  assigned_by: string | null;
  /** Тренер правил программу с тех пор, как вы её открывали. */
  coach_updated: boolean;
}

export interface ProgramDetail {
  id: number;
  name: string;
  description: string | null;
  accent_color: string | null;
  source: string;
  source_meta: Record<string, unknown> | null;
  is_archived: boolean;
  created_at: string;
  blocks: ProgramBlock[];
}

/** Методика цикла: числа живут на бэкенде, сюда приезжает только форма ввода. */
export interface Preset {
  key: string;
  name: string;
  description: string;
  /** 1 — вводится одноповторный максимум, 5 — пятиповторный. */
  max_reps: number;
  weeks: number;
  days_per_week: number;
  inputs: { key: string; label: string }[];
  variants: { key: string; label: string }[];
}

const BASE = "/api/v1";

/**
 * Отказ сервера — в отличие от обрыва связи.
 *
 * Разница определяет судьбу мутации в очереди: сеть пропала — повторяем позже,
 * сервер отказал (400/404/422) — повторять бессмысленно, надо сказать человеку.
 * Обрыв связи прилетает из fetch как TypeError и до этого класса не доходит.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Единая точка HTTP-запросов. Авторизация — заголовок `Authorization: tma <initData>`,
 * подставляется автоматически: вызывающий код про неё помнить не должен.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      // Файл уходит multipart: границу частей браузер пишет в заголовок сам.
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `tma ${initData()}`,
      ...init.headers,
    },
  });

  if (!res.ok) {
    // Бэкенд объясняет отказ в `detail` («не заданы максимумы: press»).
    // Показать код вместо этого текста — значит потерять причину.
    throw new ApiError(
      res.status,
      await detailOf(res, `${init.method ?? "GET"} ${path} → ${res.status}`),
    );
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Фотография человека — байтами.
 *
 * Не через `<img src>`: авторизация у нас в заголовке, а `<img>` заголовков не
 * шлёт. Совать `initData` в адрес нельзя — это ключ от входа, ему не место в
 * ссылке. `null` — фото нет или видеть его не положено; для экрана это одно и
 * то же: покажем букву имени.
 */
export async function fetchPhoto(userId: number): Promise<Blob | null> {
  try {
    const res = await fetch(`${BASE}/users/${userId}/photo`, {
      headers: { Authorization: `tma ${initData()}` },
    });
    return res.ok ? await res.blob() : null;
  } catch {
    return null;
  }
}

async function detailOf(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    const detail = body?.detail;
    if (typeof detail === "string") return detail;
    // Ошибка валидации FastAPI приходит списком объектов.
    if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  } catch {
    /* тело не JSON — остаётся общий текст */
  }
  return fallback;
}

export const getMe = () => request<Me>("/me");

export const getPrograms = () => request<ProgramCard[]>("/programs");

/** Готовая программа в галерее (21-retention-plan.md §4). */
export interface ProgramTemplate {
  key: string;
  name: string;
  description: string;
  level: string;
  where: "gym" | "home";
  days_per_week: number;
  weeks: number;
  day_names: string[];
  max_exercises: number;
}

export const getTemplates = () => request<ProgramTemplate[]>("/programs/templates");

/** Программа по шаблону. `schedule` — сразу в календарь с сегодняшнего дня. */
export const createFromTemplate = (key: string, schedule: boolean) =>
  request<ProgramDetail>(`/programs/templates/${key}`, {
    method: "POST",
    body: JSON.stringify({ schedule }),
  });

export const getProgram = (id: number) => request<ProgramDetail>(`/programs/${id}`);

export const deleteProgram = (id: number) =>
  request<void>(`/programs/${id}`, { method: "DELETE" });

/**
 * Сколько упражнений просим за раз. Задаём явно, а не полагаемся на дефолт
 * сервера: по этому же числу экран понимает, что выдача упёрлась в предел и
 * показанным списком библиотека не исчерпывается.
 */
export const EXERCISE_PAGE_SIZE = 100;

/**
 * Поиск по библиотеке. С запросом сервер ранжирует выдачу (слова в любом
 * порядке, мышцы, снаряды, опечатки) и подсвечивает совпадения; без запроса —
 * по алфавиту.
 */
export const getExercises = (q?: string, muscle?: string, equipment?: string) => {
  const params = new URLSearchParams({ limit: String(EXERCISE_PAGE_SIZE) });
  if (q) params.set("q", q);
  if (muscle) params.set("muscle", muscle);
  if (equipment) params.set("equipment", equipment);
  return request<Exercise[]>(`/exercises?${params}`);
};

/** «Недавние»: сделанное недавно, затем упражнения из своих программ. */
export const getMyExercises = () => request<Exercise[]>("/exercises/mine");

/** Чем заменить: та же мышца и тот же тип движения — первыми. */
export const getSimilarExercises = (exerciseId: number) =>
  request<Exercise[]>(`/exercises/${exerciseId}/similar`);

// --- Ручной конструктор -----------------------------------------------------
// Каждая правка возвращает программу целиком: держать на клиенте вторую копию
// структуры и сращивать её вручную — источник рассинхронов на ровном месте.

/** Характеристики своего упражнения — то, что спрашивает форма ExerciseForm. */
export interface ExerciseTraits {
  name_ru: string;
  primary_muscle: string;
  secondary_muscles: string[];
  equipment: string;
  movement: string;
}

/** Своё упражнение. 409 — у человека уже есть упражнение с таким названием. */
export const createExercise = (traits: ExerciseTraits) =>
  request<Exercise>("/exercises", { method: "POST", body: JSON.stringify(traits) });

export const addBlock = (programId: number, name: string) =>
  request<ProgramDetail>(`/programs/${programId}/blocks`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const removeBlock = (programId: number, blockId: number) =>
  request<ProgramDetail>(`/programs/${programId}/blocks/${blockId}`, { method: "DELETE" });

export const createProgram = (name: string) =>
  request<ProgramDetail>("/programs", { method: "POST", body: JSON.stringify({ name }) });

/**
 * Тренировка добавляется в конкретный блок.
 *
 * Без `block_id` сервер кладёт её в последний блок программы — при восьми
 * блоках это молчаливый промах мимо того, что открыто на экране.
 */
export const addDay = (programId: number, name: string, block_id?: number) =>
  request<ProgramDetail>(`/programs/${programId}/days`, {
    method: "POST",
    body: JSON.stringify({ name, block_id }),
  });

export const renameDay = (programId: number, dayId: number, name: string) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });

export const removeDay = (programId: number, dayId: number) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}`, { method: "DELETE" });

export interface ExerciseDraft {
  exercise_id: number;
  target_sets?: number | null;
  target_reps_min?: number | null;
  target_reps_max?: number | null;
  target_spec?: TargetSpec;
}

export const addExercise = (programId: number, dayId: number, draft: ExerciseDraft) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}/exercises`, {
    method: "POST",
    body: JSON.stringify(draft),
  });

/** Правка своего упражнения в библиотеке: название, мышечная группа, снаряд. */
export const updateExerciseLibrary = (
  exerciseId: number,
  patch: Partial<ExerciseTraits>,
) =>
  request<Exercise>(`/exercises/${exerciseId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

// --- Копии, ссылки, тренировка как программа (09-import-plan.md) ------------

export const duplicateProgram = (programId: number) =>
  request<ProgramDetail>(`/programs/${programId}/duplicate`, { method: "POST" });

export const duplicateBlock = (programId: number, blockId: number) =>
  request<ProgramDetail>(`/programs/${programId}/blocks/${blockId}/duplicate`, { method: "POST" });

export const duplicateDay = (programId: number, dayId: number) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}/duplicate`, { method: "POST" });

export interface ShareLink {
  token: string;
  url: string;
}

export const shareProgram = (programId: number) =>
  request<ShareLink>(`/programs/${programId}/share`, { method: "POST" });

export const unshareProgram = (programId: number) =>
  request<void>(`/programs/${programId}/share`, { method: "DELETE" });

export interface SharedProgram {
  token: string;
  name: string;
  owner_name: string;
  is_own: boolean;
  block_count: number;
  day_count: number;
  exercise_count: number;
  days: { name: string; exercises: string[] }[];
}

export const getSharedProgram = (token: string) =>
  request<SharedProgram>(`/programs/shared/${encodeURIComponent(token)}`);

export const copySharedProgram = (token: string) =>
  request<ProgramDetail>(`/programs/shared/${encodeURIComponent(token)}/copy`, { method: "POST" });

/** Проведённая тренировка → новая программа или день в своей. */
export const programFromSession = (body: { client_id: string; name?: string; program_id?: number }) =>
  request<ProgramDetail>("/programs/from-session", { method: "POST", body: JSON.stringify(body) });

/** Перенести историю тренировок из черновика Hevy / Strong. */
export const importHistory = (draft_id: string, resolution: Record<string, number>) =>
  request<{ sessions: number; sets: number }>("/programs/import/history", {
    method: "POST",
    body: JSON.stringify({ draft_id, resolution }),
  });

export const updateProgram = (programId: number, patch: { name?: string }) =>
  request<ProgramDetail>(`/programs/${programId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const updateBlock = (
  programId: number,
  blockId: number,
  patch: { name?: string; is_deload?: boolean; repeat_weeks?: number },
) =>
  request<ProgramDetail>(`/programs/${programId}/blocks/${blockId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const updateDay = (programId: number, dayId: number, patch: { name?: string }) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const updateExercise = (
  programId: number,
  dayId: number,
  rowId: number,
  patch: {
    exercise_id?: number;
    target_sets?: number | null;
    target_reps_min?: number | null;
    target_reps_max?: number | null;
    /** null очищает последовательность — так дропсет становится обычным упражнением. */
    rep_sequence?: number[] | null;
    target_spec?: TargetSpec;
    rest_sec?: number | null;
  },
) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}/exercises/${rowId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const removeExercise = (programId: number, dayId: number, rowId: number) =>
  request<ProgramDetail>(`/programs/${programId}/days/${dayId}/exercises/${rowId}`, {
    method: "DELETE",
  });

// --- Импорт из чего угодно (09-import-plan.md) -----------------------------

export interface ParsedGroup {
  sets: number;
  reps_min: number | null;
  reps_max: number | null;
  rep_sequence: number[] | null;
  is_backoff: boolean;
}

export interface ParsedExercise {
  raw_name: string;
  intensity: string | null;
  raw_sets: string;
  weight_kg?: number | null;
  rpe?: number | null;
  percent_1rm?: number | null;
  groups: ParsedGroup[];
}

export interface ParsedDay {
  name: string;
  exercises: ParsedExercise[];
}

export interface ParsedBlock {
  name: string;
  days: ParsedDay[];
}

/** Разбор программы: сервер отдаёт его в предпросмотре и получает обратно при переносе. */
export interface ParsedProgram {
  blocks: ParsedBlock[];
  warnings: string[];
}

export interface ImportPreview {
  suggested_name: string;
  block_count: number;
  day_count: number;
  exercise_count: number;
  matched: { raw_name: string; exercise_id: number; name_ru: string; via: string }[];
  unmatched: string[];
  warnings: string[];
  parsed: ParsedProgram;
  /** Откуда программа (import_xlsx, import_text…) — вернуть при переносе. */
  source?: string | null;
  /** Экспорт Hevy / Strong: история лежит в черновике на сервере. */
  draft_id?: string | null;
  history_workouts?: number;
  history_sets?: number;
  /** Названия упражнений истории (нормализованные) — их тоже надо сопоставить. */
  history_names?: string[];
  /** Книга-генератор: импортировать нечего, её надо собирать генератором. */
  is_generator?: boolean;
  detail?: string;
}

/** Файл любого поддерживаемого типа: Excel, CSV, Word, PDF с текстом, txt. */
export const previewFile = (file: File) => {
  const form = new FormData();
  form.append("file", file);
  return request<ImportPreview>("/programs/import/preview", { method: "POST", body: form });
};

/** Программа текстом: заметки, сообщение тренера. */
export const previewText = (text: string) =>
  request<ImportPreview>("/programs/import/text", {
    method: "POST",
    body: JSON.stringify({ text }),
  });

/** Ссылка на Google Таблицу или Документ. */
export const previewLink = (url: string) =>
  request<ImportPreview>("/programs/import/link", {
    method: "POST",
    body: JSON.stringify({ url }),
  });

/** Черновик, который бот разобрал, но не всё в нём узнал. */
export const getImportDraft = (draftId: string) =>
  request<ImportPreview>(`/programs/import/drafts/${encodeURIComponent(draftId)}`);

export const commitImport = (body: {
  name: string;
  parsed: ParsedProgram;
  resolution: Record<string, number>;
  source?: string | null;
}) => request<ProgramDetail>("/programs/import", { method: "POST", body: JSON.stringify(body) });

// Генератор живёт под /programs: на выходе он даёт обычную программу.
export const getPresets = () => request<Preset[]>("/programs/generator/presets");

export const generateCycle = (body: {
  preset: string;
  variant: string;
  maxes: Record<string, number>;
  name?: string;
}) =>
  request<ProgramDetail>("/programs/generator", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Проверка живости бэкенда (вне /api/v1, без авторизации). */
export async function getHealth(): Promise<boolean> {
  try {
    const res = await fetch("/health");
    return res.ok && (await res.json()).db === true;
  } catch {
    return false;
  }
}

// --- Тренировки -------------------------------------------------------------

export type SetKind = "working" | "warmup" | "drop" | "failure";

export interface LoggedSet {
  client_id: string;
  exercise_id: number;
  program_exercise_id: number | null;
  order_index: number;
  weight_kg: number | null;
  reps: number | null;
  rpe: number | null;
  rir: number | null;
  kind: SetKind;
  completed_at: string;
}

export interface PastSet {
  weight_kg: number | null;
  reps: number | null;
}

/** Вес на сегодня и причина. `why` показывается всегда — требование §2.2 ТЗ. */
export interface Recommendation {
  weight_kg: number | null;
  reps_hint: string;
  why: string;
  basis: "program" | "one_rm" | "history" | "none";
}

export interface PlanItem {
  /** null — упражнение добавлено на ходу, в программе его нет. */
  program_exercise_id: number | null;
  order_index: number;
  exercise: Exercise;
  target_sets: number | null;
  target_reps_min: number | null;
  target_reps_max: number | null;
  rep_sequence: number[] | null;
  target_spec: TargetSpec;
  rest_sec: number | null;
  notes: string | null;
  superset_group: number | null;
  is_backoff: boolean;
  /** Упражнение заменено на лету: цель от программы, история — уже от замены. */
  is_swapped: boolean;
  alternatives: { order_index: number; exercise: Exercise }[];
  previous: PastSet[];
  recommendation: Recommendation;
  /** Текущий рекорд веса. Только вес: 1ПМ и объём сервер считает при завершении. */
  record_weight_kg: number | null;
}

/** Что сделано по одному упражнению. Группировка идёт по записанным подходам. */
export interface PerformedExercise {
  exercise: Exercise;
  sets: LoggedSet[];
  working_sets: number;
  volume_kg: number;
  best_weight_kg: number | null;
  best_reps: number | null;
  e1rm_kg: number | null;
}

export interface PersonalRecord {
  exercise_id: number;
  exercise_name: string;
  kind: "max_weight" | "est_1rm" | "max_volume_session" | "max_reps_at_weight";
  value: number;
  achieved_at: string;
  /** Только в ответе завершения. null — упражнение сделано впервые. */
  previous?: number | null;
  /** Показатель по последним тренировкам, от старых к этой. Только при завершении. */
  history?: number[];
}

export interface SessionDetail {
  client_id: string;
  program_day_id: number | null;
  title: string;
  started_at: string;
  finished_at: string | null;
  notes: string | null;
  sets: LoggedSet[];
  plan: PlanItem[];
  performed: PerformedExercise[];
  /** Рекорды, взятые этой тренировкой. Непусто только в ответе на завершение. */
  new_records: PersonalRecord[];
}

export interface SessionListItem {
  client_id: string;
  program_day_id: number | null;
  title: string;
  started_at: string;
  finished_at: string | null;
  set_count: number;
  volume_kg: number;
  duration_sec: number | null;
  /** Что делали: строка на упражнение, лучший рабочий подход. */
  exercises: {
    name: string;
    working_sets: number;
    best_weight_kg: number | null;
    best_reps: number | null;
  }[];
  /** Действующие рекорды, взятые в этой тренировке. Побитый позже уходит к новой. */
  record_count: number;
}

/**
 * Идентификатор мутации. Генерится на клиенте ДО отправки: сервер по нему
 * идемпотентен, поэтому ретрай после обрыва связи безопасен (§2.4 ТЗ).
 */
export const newClientId = (): string =>
  crypto.randomUUID?.() ??
  // Telegram WebView на старых Android бывает без randomUUID.
  "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
    ).toString(16),
  );

export const startSession = (client_id: string, program_day_id: number | null) =>
  request<SessionDetail>("/sessions", {
    method: "POST",
    body: JSON.stringify({ client_id, program_day_id }),
  });

export const getSession = (clientId: string) =>
  request<SessionDetail>(`/sessions/${clientId}`);

/** Итог прошедшей тренировки для карточек-историй: рекорды пересчитаны сервером. */
export interface SessionStory {
  records: PersonalRecord[];
  muscle_counts: Record<string, number>;
  volume_kg: number;
  /** null — длительности нет: у импортированной истории начало и конец совпадают. */
  duration_sec: number | null;
  sets: number;
  exercises: number;
}

export const getSessionStory = (clientId: string) =>
  request<SessionStory>(`/sessions/${clientId}/story`);

export interface SessionShare {
  /** Чат с ботом, куда ушла карточка: оттуда её пересылают дальше. */
  chat_url: string;
  /** Готовое сообщение для нативного выбора чата. null — путь через чат бота. */
  prepared_message_id: string | null;
}

export const shareSession = (clientId: string) =>
  request<SessionShare>(`/sessions/${clientId}/share`, { method: "POST" });

// --- Тренерский слой (14-coach-plan.md) -------------------------------------

export interface CoachInvite {
  token: string;
  /** t.me/<бот>?startapp=c_<токен> — ссылка, которую тренер кидает в чат. */
  url: string;
  program_id: number | null;
}

export interface CoachInvitePreview {
  coach_name: string;
  program_name: string | null;
  already_linked: boolean;
  /** Что именно станет видно тренеру — показываем списком, дословно. */
  visible_to_coach: string[];
}

export interface Athlete {
  id: number;
  name: string;
  username: string | null;
  linked_at: string;
  /** null — ни одной тренировки: первый признак «пропал». */
  last_workout_at: string | null;
}

export interface MyCoach {
  coach_name: string | null;
  visible_to_coach: string[];
}

/** Заявка на роль тренера: уходит владельцу проекта, роль он выдаёт руками. */
export const applyForCoach = () => request<void>("/coach/apply", { method: "POST" });

export const createCoachInvite = (program_id?: number | null) =>
  request<CoachInvite>("/coach/invite", {
    method: "POST",
    body: JSON.stringify({ program_id: program_id ?? null }),
  });

export const revokeCoachInvite = () => request<void>("/coach/invite", { method: "DELETE" });

export const previewCoachInvite = (token: string) =>
  request<CoachInvitePreview>(`/coach/invite/${token}`);

export const acceptCoachInvite = (token: string) =>
  request<MyCoach>(`/coach/invite/${token}/accept`, { method: "POST" });

export const getAthletes = () => request<Athlete[]>("/coach/athletes");

export const dropAthlete = (athleteId: number) =>
  request<void>(`/coach/athletes/${athleteId}`, { method: "DELETE" });

export interface Comment {
  id: number;
  author_id: number;
  author_name: string;
  text: string;
  created_at: string;
  set_client_id: string | null;
  exercise_id: number | null;
  read_at: string | null;
}

export interface UnreadComment {
  id: number;
  author_name: string;
  text: string;
  created_at: string;
  /** По нему комментарий всплывает перед следующим выполнением упражнения. */
  exercise_id: number | null;
  session_client_id: string;
}

export const getComments = (clientId: string) =>
  request<Comment[]>(`/sessions/${clientId}/comments`);

export const addComment = (clientId: string, text: string, setClientId?: string) =>
  request<Comment>(`/sessions/${clientId}/comments`, {
    method: "POST",
    body: JSON.stringify({ text, set_client_id: setClientId ?? null }),
  });

export const getUnreadComments = () => request<UnreadComment[]>("/comments/unread");

export const markCommentRead = (id: number) =>
  request<void>(`/comments/${id}/read`, { method: "POST" });

export interface CoachFeedRow {
  athlete_id: number;
  athlete_name: string;
  session: SessionListItem;
}

/** Последние тренировки всех учеников — рабочая лента тренера. */
export const getCoachFeed = () => request<CoachFeedRow[]>("/coach/feed");

// --- Друзья (13-social-plan.md, этап 3) --------------------------------------

export interface Friend {
  id: number;
  name: string;
  username: string | null;
  /** false — человек выключил витрину: остаётся только имя. */
  visible: boolean;
  streak_weeks: number;
  last_workout_at: string | null;
  recent_records: PersonalRecord[];
}

export interface FriendInvite {
  token: string;
  url: string;
}

export interface FriendInvitePreview {
  name: string;
  already_friends: boolean;
}

export const createFriendInvite = () =>
  request<FriendInvite>("/friends/invite", { method: "POST" });

export const revokeFriendInvite = () =>
  request<void>("/friends/invite", { method: "DELETE" });

export const previewFriendInvite = (token: string) =>
  request<FriendInvitePreview>(`/friends/invite/${token}`);

export const acceptFriendInvite = (token: string) =>
  request<Friend>(`/friends/invite/${token}/accept`, { method: "POST" });

export const getFriends = () => request<Friend[]>("/friends");

/** Витрина одного друга. Не друг — 404, как и везде с чужим. */
export const getFriend = (friendId: number) => request<Friend>(`/friends/${friendId}`);

/** Недели и нагрузка друга. 404 — витрина выключена, и показывать нечего. */
export const getFriendSummary = (friendId: number) =>
  request<StatsSummary>(`/friends/${friendId}/summary`);

export const removeFriend = (friendId: number) =>
  request<void>(`/friends/${friendId}`, { method: "DELETE" });

export interface CoachPanelRow {
  athlete_id: number;
  athlete_name: string;
  last_workout_at: string | null;
  workouts_this_week: number;
  /** active — ходит; idle — пропал; stalled — стоит на месте; sick — болеет. */
  state: "active" | "idle" | "stalled" | "sick";
  detail: string | null;
}

export const getCoachPanel = () => request<CoachPanelRow[]>("/coach/panel");

export const getAthleteSessions = (athleteId: number) =>
  request<SessionListItem[]>(`/coach/athletes/${athleteId}/sessions`);

/** Сводка ученика — та же картина, что он видит у себя в профиле. */
export const getAthleteSummary = (athleteId: number) =>
  request<StatsSummary>(`/coach/athletes/${athleteId}/summary`);

export interface AssignedProgram {
  program_id: number | null;
  program_name: string | null;
}

export const assignProgram = (athleteId: number, program_id: number) =>
  request<AssignedProgram>(`/coach/athletes/${athleteId}/assign`, {
    method: "POST",
    body: JSON.stringify({ program_id }),
  });

export const unassignProgram = (athleteId: number) =>
  request<void>(`/coach/athletes/${athleteId}/assign`, { method: "DELETE" });

export const getAssignment = (athleteId: number) =>
  request<AssignedProgram>(`/coach/athletes/${athleteId}/assign`);

export const getMyCoach = () => request<MyCoach>("/coach/my");

export const leaveCoach = () => request<void>("/coach/my", { method: "DELETE" });

export const getSessions = () => request<SessionListItem[]>("/sessions");

export const logSet = (
  sessionId: string,
  body: {
    client_id: string;
    exercise_id: number;
    program_exercise_id: number | null;
    order_index: number;
    weight_kg: number | null;
    reps: number | null;
    rpe: number | null;
    kind: SetKind;
  },
) =>
  request<LoggedSet>(`/sessions/${sessionId}/sets`, {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * «Отдых окончен» от бота через `seconds`.
 *
 * keepalive — потому что зовётся в тот момент, когда окно уходит в фон, и
 * обычный запрос браузер вправе оборвать вместе со страницей.
 */
export interface NextSet {
  exercise: string | null;
  /** «Подход 3 из 4». */
  label: string | null;
  /** «60 кг × 8». */
  target: string | null;
}

export const scheduleRestAlert = (sessionId: string, seconds: number, next: NextSet | null) =>
  request<void>(`/sessions/${sessionId}/rest-alert`, {
    method: "PUT",
    body: JSON.stringify({
      seconds,
      next_exercise: next?.exercise ?? null,
      next_set: next?.label ?? null,
      next_target: next?.target ?? null,
    }),
    keepalive: true,
  });

export const cancelRestAlert = (sessionId: string) =>
  request<void>(`/sessions/${sessionId}/rest-alert`, { method: "DELETE", keepalive: true });

export const deleteSet = (sessionId: string, setId: string) =>
  request<void>(`/sessions/${sessionId}/sets/${setId}`, { method: "DELETE" });

/**
 * Завершить тренировку. `undefined` — в ней не было ни одного подхода, и
 * сервер её удалил: тренировки без подходов не существует (21-retention-plan.md §1).
 */
export const finishSession = (sessionId: string, notes?: string) =>
  request<SessionDetail | undefined>(`/sessions/${sessionId}/finish`, {
    method: "POST",
    body: JSON.stringify({ notes: notes ?? null }),
  });

/** Выйти без сохранения — только пока ничего не записано. */
export const discardSession = (sessionId: string) =>
  request<void>(`/sessions/${sessionId}`, { method: "DELETE" });

/** Вся история файлом в чат с ботом. */
export const exportHistory = () =>
  request<{ sets: number }>("/sessions/export", { method: "POST" });

export interface ExerciseSession {
  session_client_id: string;
  date: string;
  working_sets: number;
  best_weight_kg: number | null;
  best_reps: number | null;
  e1rm_kg: number | null;
  volume_kg: number;
}

export interface ExerciseStats {
  exercise: Exercise;
  records: PersonalRecord[];
  one_rm_kg: number | null;
  one_rm_source: string | null;
  estimated_1rm_kg: number | null;
  /** Новые сверху — так же читается история. График строит обратный порядок. */
  sessions: ExerciseSession[];
}

export interface WeekPoint {
  start: string;
  workouts: number;
  sets: number;
  volume_kg: number;
}

export interface MuscleShare {
  muscle: string;
  sets: number;
  volume_kg: number;
  share: number;
  /** Та же доля за предыдущий отрезок: без неё виден срез, но не сдвиг. */
  previous_share: number;
}

export interface StatsSummary {
  weeks: WeekPoint[];
  muscles: MuscleShare[];
  total_workouts: number;
  total_volume_kg: number;
  weekly_sets: MuscleWeek[];
}

export interface MainLift {
  exercise: Exercise;
  one_rm_kg: number | null;
  source: string | null;
  estimated_1rm_kg: number | null;
  /** Цель по весу. null — не поставлена. */
  goal_kg: number | null;
  /** Реально поднято: введённый максимум или самый тяжёлый рабочий подход. */
  best_lifted_kg: number | null;
  /** Взята / растёт к дате / не растёт / истории мало. */
  goal_status: "achieved" | "on_track" | "flat" | "unknown" | null;
  goal_eta: string | null;
}

/** Цель по весу. Новая цифра — новая цель: отметка «взята» сбрасывается. */
export const setGoal = (exerciseId: number, target_kg: number) =>
  request<void>(`/goals/${exerciseId}`, { method: "PUT", body: JSON.stringify({ target_kg }) });

export const removeGoal = (exerciseId: number) =>
  request<void>(`/goals/${exerciseId}`, { method: "DELETE" });

export interface BodyWeight {
  date: string;
  weight_kg: number;
}

export const getBodyWeights = (days = 180) =>
  request<BodyWeight[]>(`/body/weights?days=${days}`);

/** Одна запись на день: повторное взвешивание правит, а не добавляет. */
export const putBodyWeight = (date: string, weight_kg: number) =>
  request<BodyWeight>(`/body/weights/${date}`, {
    method: "PUT",
    body: JSON.stringify({ weight_kg }),
  });

export const deleteBodyWeight = (date: string) =>
  request<void>(`/body/weights/${date}`, { method: "DELETE" });

/** Подходов в неделю на группу за четыре недели и зона ориентира. */
export interface MuscleWeek {
  muscle: string;
  sets_per_week: number;
  zone: "low" | "ok" | "high";
}

export interface Insight {
  kind: "stagnation" | "abandoned";
  exercise: Exercise;
  /** Объяснение с числами: «застой» без цифр — гадание, которому нечем возразить. */
  detail: string;
  days: number;
  performances: number;
  from_kg: number | null;
  to_kg: number | null;
  /** Только у застоя: снизился расчётный максимум или стоит на месте. */
  trend: "down" | "flat" | null;
}

export const getInsights = () => request<Insight[]>("/stats/insights");

export interface NextWorkout {
  program_day_id: number;
  program_name: string;
  block_name: string;
  day_name: string;
  exercise_count: number;
  /** Дата из календаря. null — плана нет, предложение сделано по порядку. */
  planned_date: string | null;
  /** Сегодня по плану отдых, а показана ближайшая тренировка впереди. */
  rest_today: boolean;
}

export interface Home {
  next_workout: NextWorkout | null;
  /** Открытый период «не тренируюсь», если он накрывает сегодня. */
  absence: Absence | null;
  week_workouts: number;
  week_volume_kg: number;
  /** Недель подряд с тренировкой. 0 — серии нет, и показывать её не надо. */
  streak_weeks: number;
  last_session: SessionListItem | null;
  insights: Insight[];
  recent_records: PersonalRecord[];
  /** Семь дней от сегодня — полоса недели на главной. Пусто — плана нет. */
  week: Planned[];
}

/** Всё для главной одним запросом: четыре вызова из зала — четыре шанса на пустой экран. */
export const getHome = () => request<Home>("/stats/home");

export const getStatsSummary = (weeks = 12) =>
  request<StatsSummary>(`/stats/summary?weeks=${weeks}`);

export const getMainLifts = () => request<MainLift[]>("/stats/main-lifts");

export const getExerciseStats = (exerciseId: number) =>
  request<ExerciseStats>(`/exercises/${exerciseId}/stats`);

export const setExerciseMax = (exerciseId: number, one_rm_kg: number) =>
  request<{ exercise_id: number; one_rm_kg: number }>(`/maxes/${exerciseId}`, {
    method: "PUT",
    body: JSON.stringify({ one_rm_kg }),
  });

export const addExtraExercise = (sessionId: string, exercise_id: number) =>
  request<PlanItem>(`/sessions/${sessionId}/extra`, {
    method: "POST",
    body: JSON.stringify({ exercise_id }),
  });

export const swapExercise = (
  sessionId: string,
  program_exercise_id: number,
  exercise_id: number,
) =>
  request<PlanItem>(`/sessions/${sessionId}/swap`, {
    method: "POST",
    body: JSON.stringify({ program_exercise_id, exercise_id }),
  });


// --- Календарь (16-calendar-plan.md) ----------------------------------------

/** Что стало с плановой тренировкой. Считается на сервере, в базе не хранится. */
export type PlanStatus = "planned" | "done" | "away" | "missed";

export interface Planned {
  id: number;
  date: string;
  program_day_id: number;
  program_id: number;
  program_name: string;
  block_name: string;
  day_name: string;
  exercise_count: number;
  status: PlanStatus;
  /** Поставил тренер, а не сам. */
  by_coach: boolean;
}

export interface Absence {
  id: number;
  kind: "sick" | "vacation" | "other";
  starts_on: string;
  /** null — «пока не знаю»: период ещё не закрыт. */
  ends_on: string | null;
  note: string | null;
}

export interface CalendarSession {
  client_id: string;
  date: string;
  title: string;
  set_count: number;
  finished: boolean;
}

export interface Calendar {
  start: string;
  end: string;
  /** Сегодня в поясе человека: считать дату на клиенте — разъехаться с сервером. */
  today: string;
  planned: Planned[];
  sessions: CalendarSession[];
  absences: Absence[];
}

export interface PlanItemIn {
  date: string;
  program_day_id: number;
}

export interface LayoutIn {
  program_id: number;
  block_id?: number | null;
  /**
   * `weekdays` — привычное «пн/ср/пт». `blocks` — связками: блок программы
   * идёт подряд, потом `off_days` дней отдыха. Второй ритм нужен расписаниям
   * вида «два через два»: они сознательно едут по календарю.
   */
  rhythm?: "weekdays" | "blocks";
  /** С какой тренировки программы начать. Не задан — с начала. */
  start_day_id?: number | null;
  /** Понедельник — 0, как в Date.getDay() со сдвигом. */
  weekdays: number[];
  off_days?: number;
  start: string;
  /** Предохранитель, а не длина плана: он кончается вместе с программой. */
  weeks?: number;
}

const range = (from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const s = q.toString();
  return s ? `?${s}` : "";
};

export const getCalendar = (from?: string, to?: string) =>
  request<Calendar>(`/plan${range(from, to)}`);

export const previewLayout = (payload: LayoutIn) =>
  request<PlanItemIn[]>("/plan/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });

/** Ритм уходит вместе с раскладкой: по нему сдвигается хвост после болезни. */
export interface PlanRhythm {
  rhythm: "weekdays" | "blocks";
  weekdays: number[];
  off_days: number;
}

export const savePlan = (items: PlanItemIn[], replace_from?: string, rhythm?: PlanRhythm) =>
  request<Calendar>("/plan", {
    method: "POST",
    body: JSON.stringify({ items, replace_from: replace_from ?? null, ...(rhythm ?? {}) }),
  });

export const movePlanned = (id: number, patch: { date?: string; program_day_id?: number }) =>
  request<Planned>(`/plan/${id}`, { method: "PATCH", body: JSON.stringify(patch) });

export const deletePlanned = (id: number) =>
  request<void>(`/plan/${id}`, { method: "DELETE" });

/** Убрать несделанный план начиная с даты. Без даты — с сегодняшнего дня. */
export const clearPlan = (from?: string) =>
  request<{ removed: number }>(`/plan${range(from)}`, { method: "DELETE" });

export const reflowPlan = (from_date: string, resume_from: string) =>
  request<{ moved: number; first_date: string | null }>("/plan/reflow", {
    method: "POST",
    body: JSON.stringify({ from_date, resume_from }),
  });

export const getAbsences = () => request<Absence[]>("/absences");

export const openAbsence = (payload: {
  kind?: string;
  starts_on: string;
  ends_on?: string | null;
  note?: string | null;
}) => request<Absence>("/absences", { method: "POST", body: JSON.stringify(payload) });

export const closeAbsence = (id: number, ends_on?: string) =>
  request<Absence>(`/absences/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ ends_on: ends_on ?? null }),
  });

export const deleteAbsence = (id: number) =>
  request<void>(`/absences/${id}`, { method: "DELETE" });

// Тренер планирует ученику тем же мастером — меняется только адрес.
export const previewAthleteLayout = (athleteId: number, payload: LayoutIn) =>
  request<PlanItemIn[]>(`/coach/athletes/${athleteId}/plan/preview`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const saveAthletePlan = (
  athleteId: number,
  items: PlanItemIn[],
  replace_from?: string,
  rhythm?: PlanRhythm,
) =>
  request<Calendar>(`/coach/athletes/${athleteId}/plan`, {
    method: "POST",
    body: JSON.stringify({ items, replace_from: replace_from ?? null, ...(rhythm ?? {}) }),
  });

export const getAthletePlan = (athleteId: number, from?: string, to?: string) =>
  request<Calendar>(`/coach/athletes/${athleteId}/plan${range(from, to)}`);
